import React, { useEffect, useState } from 'react';
import { ref, onValue, get } from 'firebase/database';
import { db } from '../../firebase/config';
import {
    FaRupeeSign, FaCalendarAlt, FaChartLine, FaDownload,
    FaSpinner, FaUsers, FaCheckCircle, FaClock, FaFilter,
    FaMapMarkerAlt, FaFileInvoice, FaSortAmountDown, FaSortAmountUp,
    FaEye, FaTimes, FaPrint
} from 'react-icons/fa';

const TrainingPaymentReport = () => {
    const [trainings, setTrainings] = useState([]);
    const [paymentData, setPaymentData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [referrerNames, setReferrerNames] = useState({});

    // Filters
    const [selectedTraining, setSelectedTraining] = useState('all');
    const [dateFilter, setDateFilter] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [sortBy, setSortBy] = useState('date');
    const [sortOrder, setSortOrder] = useState('desc');

    const [showModal, setShowModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);

    const [summary, setSummary] = useState({
        totalParticipants: 0,
        totalPaid: 0,
        totalPending: 0,
        totalAmount: 0,
        paidAmount: 0,
        pendingAmount: 0
    });

    const handleViewPayment = (payment) => {
        setSelectedPayment(payment);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedPayment(null);
    };

    // Fetch trainings and payment data
    useEffect(() => {
        const trainingRef = ref(db, 'HTAMS/company/trainings');
        const unsubscribe = onValue(trainingRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const trainingsList = [];
                const payments = [];

                Object.entries(data).forEach(([trainingId, trainingData]) => {
                    const training = {
                        id: trainingId,
                        location: trainingData.location || 'N/A',
                        venue: trainingData.venue || 'N/A',
                        startDate: trainingData.startDate,
                        fees: trainingData.fees || 0,
                        trainerName: trainingData.trainerName || 'N/A'
                    };
                    trainingsList.push(training);

                    if (trainingData.participants) {
                        Object.entries(trainingData.participants).forEach(([participantId, participant]) => {
                            const rawStatus = participant.paymentStatus || participant.paymentDetails?.payment_status || 'pending';
                            const paymentStatus = (rawStatus === 'completed' || rawStatus === 'success') ? 'success' : rawStatus;

                            let referredBy = null;
                            if (participant.referredBy) {
                                if (typeof participant.referredBy === 'object' && participant.referredBy !== null) {
                                    if (participant.referredBy.id || participant.referredBy.name) {
                                        referredBy = { id: participant.referredBy.id || '', name: participant.referredBy.name || '' };
                                    } else {
                                        const firstValue = Object.values(participant.referredBy).find(val => typeof val === 'string');
                                        referredBy = { id: firstValue || '', name: '' };
                                    }
                                } else if (typeof participant.referredBy === 'string') {
                                    referredBy = { id: participant.referredBy, name: '' };
                                }
                            }

                            payments.push({
                                id: participantId,
                                trainingId,
                                trainingLocation: trainingData.location || 'N/A',
                                trainingStartDate: trainingData.startDate,
                                trainingFees: trainingData.fees || 0,
                                participantName: participant.name || 'N/A',
                                participantEmail: participant.email || 'N/A',
                                participantPhone: participant.phone || participant.mobile || 'N/A',
                                firmName: participant.firmName || 'N/A',
                                registeredAt: participant.registeredAt || participant.joinedAt || participant.createdAt || Date.now(),
                                paymentStatus: paymentStatus,
                                paymentId: participant.paymentDetails?.razorpay_payment_id || participant.paymentDetails?.payment_link_id || participant.paymentId || 'N/A',
                                amountPaid: participant.feePaid || participant.paymentDetails?.amount_paid || participant.amountPaid || 0,
                                expectedAmount: trainingData.fees || 0,
                                mode: participant.paymentDetails?.payment_method || participant.mode || 'N/A',
                                referredBy: referredBy
                            });
                        });
                    }
                });

                setTrainings(trainingsList);
                setPaymentData(payments);
                setFilteredData(payments);
                calculateSummary(payments);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const calculateSummary = (data) => {
        setSummary({
            totalParticipants: data.length,
            totalPaid: data.filter(p => p.paymentStatus === 'success').length,
            totalPending: data.filter(p => p.paymentStatus === 'pending').length,
            totalAmount: data.reduce((sum, p) => sum + p.expectedAmount, 0),
            paidAmount: data.filter(p => p.paymentStatus === 'success').reduce((sum, p) => sum + p.amountPaid, 0),
            pendingAmount: data.filter(p => p.paymentStatus === 'pending').reduce((sum, p) => sum + p.expectedAmount, 0)
        });
    };

    useEffect(() => {
        const fetchAllUsers = async () => {
            try {
                const usersRef = ref(db, 'HTAMS/users');
                const snapshot = await get(usersRef);
                if (snapshot.exists()) {
                    const users = snapshot.val();
                    const userMap = {};
                    Object.entries(users).forEach(([userId, userData]) => {
                        if (userData) {
                            userMap[userId] = {
                                name: userData.name || userData.displayName || '',
                                phone: userData.phone || userData.mobile || '',
                                email: userData.email || ''
                            };
                        }
                    });
                    setReferrerNames(userMap);
                }
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };
        fetchAllUsers();
    }, []);

    useEffect(() => {
        let filtered = [...paymentData];

        if (selectedTraining !== 'all') {
            filtered = filtered.filter(p => p.trainingId === selectedTraining);
        }

        if (dateFilter === 'custom' && startDate && endDate) {
            const start = new Date(startDate).getTime();
            const end = new Date(endDate).getTime();
            filtered = filtered.filter(p => {
                const regDate = new Date(p.registeredAt).getTime();
                return regDate >= start && regDate <= end;
            });
        } else if (dateFilter === 'today') {
            const today = new Date().setHours(0, 0, 0, 0);
            filtered = filtered.filter(p => new Date(p.registeredAt).setHours(0, 0, 0, 0) === today);
        } else if (dateFilter === 'week') {
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            filtered = filtered.filter(p => new Date(p.registeredAt) >= weekAgo);
        } else if (dateFilter === 'month') {
            const monthAgo = new Date();
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            filtered = filtered.filter(p => new Date(p.registeredAt) >= monthAgo);
        }

        filtered.sort((a, b) => {
            let comparison = 0;
            switch (sortBy) {
                case 'date': comparison = new Date(a.registeredAt) - new Date(b.registeredAt); break;
                case 'training': comparison = (a.trainingStartDate || '').localeCompare(b.trainingStartDate || ''); break;
                case 'amount': comparison = a.amountPaid - b.amountPaid; break;
                case 'name': comparison = a.participantName.localeCompare(b.participantName); break;
                default: comparison = 0;
            }
            return sortOrder === 'asc' ? comparison : -comparison;
        });

        setFilteredData(filtered);
        calculateSummary(filtered);
    }, [selectedTraining, dateFilter, startDate, endDate, sortBy, sortOrder, paymentData]);

    const exportToCSV = () => {
        const headers = [
            'Training Location', 'Training Date', 'Participant Name', 'Email', 'Phone',
            'Firm Name', 'Mode', 'Expected Amount', 'Amount Paid', 'Payment Status',
            'Payment ID', 'Registered Date', 'Referred By'
        ];
        const rows = filteredData.map(p => [
            p.trainingLocation,
            p.trainingStartDate ? new Date(p.trainingStartDate).toLocaleDateString() : 'N/A',
            p.participantName,
            p.participantEmail,
            p.participantPhone,
            p.firmName,
            p.mode,
            p.expectedAmount,
            p.amountPaid,
            p.paymentStatus,
            p.paymentId,
            new Date(p.registeredAt).toLocaleString(),
            p.referredBy ? `${p.referredBy.name} (${p.referredBy.id})` : 'Direct'
        ]);
        rows.push([], ['SUMMARY']);
        rows.push(['Total Participants', summary.totalParticipants], ['Paid Participants', summary.totalPaid]);
        rows.push(['Pending Participants', summary.totalPending], ['Total Expected Amount', summary.totalAmount]);
        rows.push(['Total Paid Amount', summary.paidAmount], ['Total Pending Amount', summary.pendingAmount]);

        const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `training-report-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const getReferrerName = (referredBy) => {
        if (!referredBy) return 'Direct';
        let referrerId = '';
        if (typeof referredBy === 'string') referrerId = referredBy;
        else if (referredBy.id) referrerId = referredBy.id;
        else if (typeof referredBy === 'object') {
            const possibleId = Object.values(referredBy).find(val => typeof val === 'string' && val.length > 10);
            if (possibleId) referrerId = possibleId;
        }

        if (referrerId && referrerNames[referrerId]) return referrerNames[referrerId].name || referrerId;
        if (referredBy.name) return referredBy.name;
        if (referrerId) return referrerId;
        return 'Direct';
    };

    return (
        <div className="report-container">
            {/* Header */}
            <div className="compact-header">
                <div>
                    <h1 className="compact-title">
                        <FaFileInvoice className="text-blue-600" />
                        Training Payment Report
                    </h1>
                    <p className="compact-subtitle">Comprehensive payment tracking and collection analysis</p>
                </div>
                <div className="header-actions">
                    <button className="compact-btn outline" onClick={exportToCSV}>
                        <FaDownload className="mr-2" /> Export CSV
                    </button>
                    <button className="compact-btn outline" onClick={() => window.print()}>
                        <FaPrint className="mr-2" /> Print
                    </button>
                </div>
            </div>

            {/* Compact Stats */}
            <div className="compact-stats-grid">
                <div className="compact-stat-card active ring-blue-500">
                    <div className="stat-value">{summary.totalParticipants}</div>
                    <div className="stat-label">Total Participants</div>
                </div>
                <div className="compact-stat-card active ring-emerald-500">
                    <div className="stat-value">{summary.totalPaid}</div>
                    <div className="stat-label">Paid</div>
                </div>
                <div className="compact-stat-card active ring-amber-500">
                    <div className="stat-value">{summary.totalPending}</div>
                    <div className="stat-label">Pending</div>
                </div>
                <div className="compact-stat-card active ring-violet-500">
                    <div className="stat-value">₹{summary.totalAmount.toLocaleString()}</div>
                    <div className="stat-label">Total Expected</div>
                </div>
                <div className="compact-stat-card active ring-blue-500">
                    <div className="stat-value">₹{summary.paidAmount.toLocaleString()}</div>
                    <div className="stat-label">Collected</div>
                </div>
                <div className="compact-stat-card active ring-rose-500">
                    <div className="stat-value">₹{summary.pendingAmount.toLocaleString()}</div>
                    <div className="stat-label">Pending Amount</div>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="filters-bar">
                <div className="filter-item">
                    <FaFilter className="filter-icon" />
                    <select
                        value={selectedTraining}
                        onChange={(e) => setSelectedTraining(e.target.value)}
                        className="compact-select"
                    >
                        <option value="all">All Trainings</option>
                        {trainings.map(t => (
                            <option key={t.id} value={t.id}>
                                {t.location} {t.startDate ? `(${new Date(t.startDate).toLocaleDateString()})` : '(No Date)'}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="filter-item">
                    <FaCalendarAlt className="filter-icon" />
                    <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="compact-select">
                        <option value="all">All Dates</option>
                        <option value="today">Today</option>
                        <option value="week">7 Days</option>
                        <option value="month">30 Days</option>
                        <option value="custom">Custom</option>
                    </select>
                </div>
                {dateFilter === 'custom' && (
                    <div className="flex gap-2">
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="compact-input" />
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="compact-input" />
                    </div>
                )}
                <div className="filter-item">
                    <FaSortAmountDown className="filter-icon" />
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="compact-select">
                        <option value="date">Reg. Date</option>
                        <option value="amount">Amount</option>
                        <option value="name">Name</option>
                    </select>
                </div>
                <button className="compact-view-btn" onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}>
                    {sortOrder === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
                </button>
            </div>

            {/* Data Table */}
            <div className="compact-table-card">
                <div className="table-responsive">
                    <table className="compact-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Training</th>
                                <th>Participant</th>
                                <th>Referred By</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th className="text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" className="text-center py-8"><FaSpinner className="animate-spin inline mr-2" /> Loading...</td></tr>
                            ) : filteredData.length === 0 ? (
                                <tr><td colSpan="7" className="text-center py-8 text-slate-400">No records found.</td></tr>
                            ) : (
                                filteredData.map((p, i) => (
                                    <tr key={p.id}>
                                        <td className="text-gray-400 text-xs text-center">{i + 1}</td>
                                        <td>
                                            <div className="flex flex-col">
                                                <span className="font-semibold text-xs text-slate-700">{p.trainingLocation}</span>
                                                <span className="text-xs text-slate-400">{p.trainingStartDate ? new Date(p.trainingStartDate).toLocaleDateString() : '-'}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex flex-col">
                                                <span className="font-medium text-slate-800 text-sm">{p.participantName}</span>
                                                <span className="text-xs text-slate-500">{p.participantPhone}</span>
                                            </div>
                                        </td>
                                        <td className="text-sm text-slate-600">{getReferrerName(p.referredBy)}</td>
                                        <td className="text-sm font-bold text-slate-700">₹{p.amountPaid.toLocaleString()}</td>
                                        <td>
                                            <span className={`status-pill ${p.paymentStatus === 'success' ? 'success' : 'pending'}`}>
                                                {p.paymentStatus}
                                            </span>
                                        </td>
                                        <td className="text-right">
                                            <button className="compact-view-btn" onClick={() => handleViewPayment(p)}>
                                                <FaEye /> View
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal - Restored to Original Design */}
            {showModal && selectedPayment && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content payment-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Payment Details</h2>
                            <button className="close-button" onClick={closeModal}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-grid">
                                <div className="detail-item">
                                    <label>Participant Name</label>
                                    <p>{selectedPayment.participantName}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Email</label>
                                    <p>{selectedPayment.participantEmail}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Phone</label>
                                    <p>{selectedPayment.participantPhone}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Firm Name</label>
                                    <p>{selectedPayment.firmName}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Training Location</label>
                                    <p>{selectedPayment.trainingLocation}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Training Date</label>
                                    <p>{selectedPayment.trainingStartDate ? new Date(selectedPayment.trainingStartDate).toLocaleDateString() : 'N/A'}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Payment Status</label>
                                    <span className={`status-badge ${selectedPayment.paymentStatus}`}>
                                        {selectedPayment.paymentStatus}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <label>Amount Paid</label>
                                    <p>₹{selectedPayment.amountPaid.toLocaleString()}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Expected Amount</label>
                                    <p>₹{selectedPayment.expectedAmount.toLocaleString()}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Payment Mode</label>
                                    <p>{selectedPayment.mode}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Payment ID</label>
                                    <p>{selectedPayment.paymentId}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Registered At</label>
                                    <p>{new Date(selectedPayment.registeredAt).toLocaleString()}</p>
                                </div>
                                <div className="detail-item">
                                    <label>Referred By</label>
                                    <p>{getReferrerName(selectedPayment.referredBy)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .report-container { padding: 1.5rem; background: #f1f5f9; min-height: 10vh; font-family: 'Inter', sans-serif; color: #1e293b; }
                
                /* Header */
                .compact-header { background: white; padding: 1.25rem 1.5rem; border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; border: 1px solid #e2e8f0; flex-wrap: wrap; gap: 1rem; }
                .compact-title { font-size: 1.5rem; font-weight: 700; color: #0f172a; display: flex; align-items: center; gap: 0.75rem; margin: 0; }
                .compact-subtitle { font-size: 0.875rem; color: #64748b; margin: 0.25rem 0 0 0; }
                .header-actions { display: flex; gap: 0.75rem; }
                
                /* Stats Grid */
                .compact-stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 1rem; margin-bottom: 2rem; }
                .compact-stat-card { background: white; padding: 1rem 0.75rem; border-radius: 12px; border: 1px solid #e2e8f0; display: flex; flex-direction: column; align-items: center; text-align: center; justify-content: center; position: relative; }
                .compact-stat-card.active { border-bottom-width: 3px; }
                .ring-blue-500 { border-bottom-color: #3b82f6; }
                .ring-emerald-500 { border-bottom-color: #10b981; }
                .ring-amber-500 { border-bottom-color: #f59e0b; }
                .ring-violet-500 { border-bottom-color: #8b5cf6; }
                .ring-rose-500 { border-bottom-color: #f43f5e; }
                
                .stat-value { font-size: 1.25rem; font-weight: 800; color: #0f172a; margin-bottom: 4px; }
                .stat-label { font-size: 0.7rem; color: #64748b; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; }

                /* Filters */
                .filters-bar { display: flex; gap: 1rem; margin-bottom: 1.5rem; flex-wrap: wrap; align-items: center; background: white; padding: 0.75rem; border-radius: 8px; border: 1px solid #e2e8f0; }
                .filter-item { position: relative; display: flex; align-items: center; }
                .filter-icon { position: absolute; left: 10px; color: #64748b; font-size: 0.8rem; z-index: 1; }
                .compact-select, .compact-input { padding: 8px 12px 8px 30px; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 0.875rem; color: #334155; outline: none; background: white; min-width: 140px; }
                .compact-input { padding-left: 10px; min-width: auto; }

                /* Table */
                .compact-table-card { background: white; border-radius: 16px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); border: 1px solid #e2e8f0; overflow: hidden; }
                .compact-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
                .compact-table th { background: white; padding: 1rem 1.5rem; text-align: left; font-size: 0.75rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #f1f5f9; }
                .compact-table td { padding: 0.75rem 1.5rem; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
                
                .status-pill { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 0.7rem; font-weight: 600; text-transform: capitalize; }
                .status-pill.success { background: #d1fae5; color: #065f46; }
                .status-pill.pending { background: #fef3c7; color: #92400e; }

                /* Buttons */
                .compact-btn { padding: 0.5rem 1rem; border-radius: 6px; font-size: 0.85rem; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; border: 1px solid #e2e8f0; background: white; color: #475569; }
                .compact-btn:hover { background: #f8fafc; border-color: #cbd5e1; }
                .compact-view-btn { background: none; border: 1px solid #e2e8f0; padding: 4px 10px; border-radius: 6px; color: #475569; font-size: 0.75rem; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; }
                .compact-view-btn:hover { background: #f1f5f9; }

                /* Original Modal Styles Restored */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                    backdrop-filter: blur(5px);
                }

                .modal-content.payment-modal {
                    background: white;
                    border-radius: 20px;
                    width: 90%;
                    max-width: 600px;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    animation: slideUp 0.3s ease-out;
                }

                .modal-header {
                    padding: 24px;
                    border-bottom: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    position: sticky;
                    top: 0;
                    background: white;
                    z-index: 10;
                }

                .modal-header h2 {
                    margin: 0;
                    font-size: 1.5rem;
                    color: #1a202c;
                }

                .close-button {
                    background: none;
                    border: none;
                    color: #718096;
                    font-size: 1.5rem;
                    cursor: pointer;
                    padding: 4px;
                    border-radius: 50%;
                    transition: all 0.2s;
                }

                .close-button:hover {
                    background: #f7fafc;
                    color: #2d3748;
                }

                .modal-body { padding: 24px; }
                .detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                .detail-item { display: flex; flex-direction: column; gap: 4px; }
                .detail-item label { font-size: 0.85rem; color: #718096; font-weight: 600; text-transform: uppercase; }
                .detail-item p { margin: 0; font-size: 1rem; color: #2d3748; font-weight: 500; word-break: break-word; }

                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                @media (max-width: 640px) {
                    .detail-grid { grid-template-columns: 1fr; }
                }

                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: uppercase;
                }
                .status-badge.success { background: rgba(72, 187, 120, 0.1); color: #2f855a; }
                .status-badge.pending { background: rgba(237, 137, 54, 0.1); color: #c05621; }
                .status-badge.failed { background: rgba(245, 101, 101, 0.1); color: #c53030; }
            `}</style>
        </div>
    );
};

export default TrainingPaymentReport;
